import { create } from "zustand";
import { supabase } from "@/lib/supabase";
import {
	DensitySettings,
	TabConfig,
} from "@/dentlab/src/types/tab-config.types";
import { Json } from "@/lib/supabase/supabaseTypesImport";
import { showNotification } from "../Central/selectors";
import { useCentralStore } from "../Central";
import { SYSTEM_DEFAULT_TAB_CONFIG } from "@/dentlab/src/config/default-tab-config";

interface JobTabsConfig {
	id: string;
	organization_id: string;
	name: string;
	description?: string | null;
	tabs: Json;
	is_default?: boolean;
	created_at: string;
	modified_at?: string;
	updated_at?: string;
	density_padding?: number;
	density_gap?: number;
	density_input_height?: number;
	density_font_size?: number;
}

interface AutosaveStatus {
	saving: boolean;
	hasUnsavedChanges: boolean;
	lastSavedAt: Date | null;
}

interface TabConfigState {
	configurations: JobTabsConfig[];
	selectedConfigId: string | null;
	loading: boolean;
	defaultConfigId: string | null;
	showSidebar: boolean;
	autosaveStatus: AutosaveStatus;

	// Actions
	fetchConfigurations: (organizationId: string) => Promise<void>;
	createConfiguration: (
		organizationId: string,
		name: string
	) => Promise<void>;
	updateConfiguration: (id: string, data: TabConfig[]) => void;
	renameConfiguration: (id: string, name: string) => Promise<void>;
	deleteConfiguration: (id: string) => Promise<void>;
	selectConfiguration: (id: string) => void;
	setAsDefault: (configId: string | null) => Promise<void>;
	toggleSidebar: () => void;
	duplicateConfiguration: (id: string) => Promise<void>;
	updateDensity: (id: string, density: DensitySettings) => Promise<void>;
}

const AUTOSAVE_DEBOUNCE_MS = 800;
const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useTabConfigStore = create<TabConfigState>((set, get) => ({
	configurations: [],
	selectedConfigId: null,
	loading: false,
	defaultConfigId: null,
	showSidebar: true,
	autosaveStatus: {
		saving: false,
		hasUnsavedChanges: false,
		lastSavedAt: null,
	},

	fetchConfigurations: async (organizationId) => {
		set({ loading: true });
		const { data, error } = await supabase
			.from("job_tabs_configs")
			.select("*")
			.eq("organization_id", organizationId)
			.order("created_at", { ascending: false });

		if (!error && data) {
			const configs = (data as JobTabsConfig[]).map((config) => {
				const tabs = config.tabs as unknown as TabConfig[];
				if (!tabs?.some((t) => t.type === "total")) {
					const maxOrder =
						tabs?.reduce((max, t) => Math.max(max, t.order), -1) ??
						-1;
					return {
						...config,
						tabs: [
							...(tabs || []),
							{
								id: "system_total",
								icon: "Sigma",
								type: "total" as const,
								color: "#689689",
								order: maxOrder + 1,
								isDefaultActive: true,
							},
						] as unknown as Json,
					};
				}
				return config;
			});

			const orgDefaultId =
				useCentralStore.getState().organization
					?.default_job_tabs_config_id;
			set({
				configurations: configs,
				loading: false,
				defaultConfigId: orgDefaultId || null,
			});
			if (data.length > 0 && !get().selectedConfigId) {
				set({ selectedConfigId: data[0].id });
			}
		} else {
			set({ loading: false });
		}
	},

	createConfiguration: async (organizationId, name) => {
		// Create a copy of the default configuration with unique IDs
		const timestamp = Date.now();
		const defaultData: TabConfig[] = SYSTEM_DEFAULT_TAB_CONFIG.map(
			(tab, index) => ({
				...tab,
				id: (timestamp + index).toString(),
			})
		);

		const { data, error } = await supabase
			.from("job_tabs_configs")
			.insert({
				organization_id: organizationId,
				name,
				tabs: defaultData as unknown as Json[],
				density_padding: 4,
				density_gap: 2,
				density_input_height: 28,
				density_font_size: 12,
			})
			.select()
			.single();

		if (!error && data) {
			await get().fetchConfigurations(organizationId);
			set({ selectedConfigId: data.id });
		}
	},

	updateConfiguration: (id, data) => {
		set((state) => ({
			configurations: state.configurations.map((c) =>
				c.id === id ? { ...c, tabs: data as unknown as Json } : c
			),
			autosaveStatus: {
				saving: false,
				hasUnsavedChanges: true,
				lastSavedAt: state.autosaveStatus.lastSavedAt,
			},
		}));

		const existing = saveTimers.get(id);
		if (existing) clearTimeout(existing);

		saveTimers.set(
			id,
			setTimeout(async () => {
				saveTimers.delete(id);
				set((state) => ({
					autosaveStatus: {
						...state.autosaveStatus,
						saving: true,
					},
				}));

				const { error } = await supabase
					.from("job_tabs_configs")
					.update({
						tabs: data as unknown as Json[],
						modified_at: new Date().toISOString(),
					})
					.eq("id", id);

				if (error) {
					set((state) => ({
						autosaveStatus: {
							...state.autosaveStatus,
							saving: false,
						},
					}));
					showNotification({
						message:
							"Konfiguration konnte nicht gespeichert werden",
						type: "error",
					});
					return;
				}

				set({
					autosaveStatus: {
						saving: false,
						hasUnsavedChanges: false,
						lastSavedAt: new Date(),
					},
				});
			}, AUTOSAVE_DEBOUNCE_MS)
		);
	},

	renameConfiguration: async (id, name) => {
		const { error } = await supabase
			.from("job_tabs_configs")
			.update({
				name,
				modified_at: new Date().toISOString(),
			})
			.eq("id", id);

		if (!error) {
			// Update local state
			set((state) => ({
				configurations: state.configurations.map((c) =>
					c.id === id ? { ...c, name } : c
				),
			}));
		}
	},

	deleteConfiguration: async (id) => {
		const { error } = await supabase
			.from("job_tabs_configs")
			.delete()
			.eq("id", id);

		if (!error) {
			set((state) => ({
				configurations: state.configurations.filter((c) => c.id !== id),
				selectedConfigId:
					state.selectedConfigId === id
						? null
						: state.selectedConfigId,
			}));
		}
	},

	selectConfiguration: (id) => set({ selectedConfigId: id }),

	setAsDefault: async (configId) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) return;

		const { error } = await supabase
			.from("organizations")
			.update({ default_job_tabs_config_id: configId })
			.eq("id", organizationId);

		if (!error) {
			// Update the central store
			useCentralStore
				.getState()
				.updateOrganization("default_job_tabs_config_id", configId);

			// Update local state
			set({ defaultConfigId: configId });

			showNotification({
				message: configId
					? "Standard-Ansicht festgelegt"
					: "Standard-Ansicht entfernt",
				type: "success",
			});
		}
	},
	duplicateConfiguration: async (id) => {
		const source = get().configurations.find((c) => c.id === id);
		if (!source) return;

		const timestamp = Date.now();
		const sourceTabs = source.tabs as unknown as TabConfig[];
		const clonedTabs = sourceTabs.map((tab, index) => ({
			...tab,
			id: (timestamp + index).toString(),
		}));

		const { data, error } = await supabase
			.from("job_tabs_configs")
			.insert({
				organization_id: source.organization_id,
				name: `${source.name} (Kopie)`,
				tabs: clonedTabs as unknown as Json[],
				density_padding: source.density_padding,
				density_gap: source.density_gap,
				density_input_height: source.density_input_height,
				density_font_size: source.density_font_size,
			})
			.select()
			.single();

		if (!error && data) {
			await get().fetchConfigurations(source.organization_id);
			set({ selectedConfigId: data.id });
		}
	},

	toggleSidebar: () => {
		set((state) => ({ showSidebar: !state.showSidebar }));
	},
	updateDensity: async (id, density) => {
		const update = {
			density_padding: density.padding,
			density_gap: density.gap,
			density_input_height: density.inputHeight,
			density_font_size: density.fontSize,
		};
		const { error } = await supabase
			.from("job_tabs_configs")
			.update(update)
			.eq("id", id);

		if (!error) {
			set((state) => ({
				configurations: state.configurations.map((c) =>
					c.id === id ? { ...c, ...update } : c
				),
			}));
		}
	},
}));
